import { Api } from '@monorepo/shared/apiClient';
import {
  ProjectUsersCacheEvents,
  projectUsersCacheRegister,
} from '@monorepo/shared/cacheRegisters/users/projectUsersCacheRegister';
import { getKeyParamsForInvalidation } from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

const KEY_START = 'project-users';

export const createKey = ({ projectId }: Api.FetchAllUsersOnProjectProps) =>
  [KEY_START, projectId] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchAllUsersOnProject
>;

const fetcher: Fetcher = (_, projectId) =>
  Api.fetchAllUsersOnProject({ projectId });

type UseProjectUsersProps = Partial<Api.FetchAllUsersOnProjectProps> & {
  config?: QueryConfig<Api.FetchUsersResponse, Api.ErrorResponse>;
};

/**
 * This hook loads full user objects, which can be a lot of data for projects with a large number of users
 * (e.g. GMR has 30k users, and they each belong to every site).
 *
 * If you only need names or emails, consider `useProjectUserRefs`.
 */
export function useProjectUsers({
  config: inputConfig,
  projectId,
}: UseProjectUsersProps) {
  const isEnabled = !!projectId;
  const key = isEnabled ? createKey({ projectId }) : undefined;
  const config = {
    enabled: isEnabled,
    ...inputConfig,
  };
  const { data, ...queryInfo } = useQuery(key, fetcher, config);
  return {
    ...queryInfo,
    users: data,
  };
}

type InvalidateCacheParams = {
  projectId: string;
};

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([KEY_START, keyParams.projectId]),
  );
}

projectUsersCacheRegister(
  [
    ProjectUsersCacheEvents.CREATE,
    ProjectUsersCacheEvents.UPDATE,
    ProjectUsersCacheEvents.DELETE,
  ],
  {
    hookName: 'useProjectUsers',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);
